import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Search, Merge, Tag } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { Pagination } from '../components/shared/Pagination';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('delete_product');
  const canMerge = hasPermission('merge_products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeInventory, setMergeInventory] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: 'kg',
    base_price: '',
    reorder_level: '10',
    tax_bracket: 'B',
    barcode: ''
  });

  const units = ['kg', 'piece', 'tray', 'bunch', 'g', 'lbs', 'dozen', 'liters', 'ml', 'boxes'];
  const taxBrackets = [
    { value: 'A', label: 'A (0%)' },
    { value: 'B', label: 'B (18% inclusive)' },
    { value: 'C', label: 'C (0%)' },
    { value: 'D', label: 'D (0%)' }
  ];

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/product-categories`);
      setCategories(response.data);
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0] }));
      }
    } catch (error) {
      setCategories(['Vegetables', 'Fruits', 'Meat', 'Dairy', 'Bakery', 'Beverages', 'Dry Goods', 'Herbs', 'Other']);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API_URL}/product-categories?name=${encodeURIComponent(newCategory.trim())}`);
      toast.success(`Category "${newCategory}" created`);
      setNewCategory('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/product-categories/${encodeURIComponent(name)}`);
      toast.success(`Category "${name}" deleted`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts([...response.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        base_price: parseFloat(formData.base_price),
        reorder_level: parseFloat(formData.reorder_level),
        barcode: formData.barcode?.trim() || null
      };
      
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, data);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/products`, data);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      unit: product.unit,
      base_price: product.base_price.toString(),
      reorder_level: product.reorder_level.toString(),
      tax_bracket: product.tax_bracket || 'B',
      barcode: product.barcode || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/products/${id}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} products?`)) return;
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/products/${id}`)));
      toast.success(`Deleted ${selectedIds.length} products`);
      setSelectedIds([]);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete products');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredProducts.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: categories.length > 0 ? categories[0] : '',
      unit: 'kg',
      base_price: '',
      reorder_level: '10',
      tax_bracket: 'B',
      barcode: ''
    });
    setEditingProduct(null);
  };

  const handleMergeProducts = async () => {
    if (!mergeSource || !mergeTarget) {
      toast.error('Please select both source and target products');
      return;
    }
    if (mergeSource === mergeTarget) {
      toast.error('Source and target products must be different');
      return;
    }
    const sourceName = products.find(p => p.id === mergeSource)?.name;
    const targetName = products.find(p => p.id === mergeTarget)?.name;
    if (!window.confirm(`Merge "${sourceName}" into "${targetName}"? This will transfer all orders, purchases, and price lists from "${sourceName}" to "${targetName}" and delete "${sourceName}".`)) return;
    
    try {
      const params = new URLSearchParams();
      params.append('source_product_id', mergeSource);
      params.append('target_product_id', mergeTarget);
      params.append('merge_inventory', mergeInventory.toString());
      
      const response = await axios.post(`${API_URL}/products/merge?${params.toString()}`);
      toast.success(response.data.message || 'Products merged successfully');
      setMergeDialogOpen(false);
      setMergeSource('');
      setMergeTarget('');
      setMergeInventory(true);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge products');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.barcode || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProducts = filteredProducts.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Products</h1>
          <p className="text-muted-foreground">Manage your product catalog</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && canDelete && (
            <Button variant="destructive" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.length})
            </Button>
          )}
          {canMerge && (
            <Button variant="outline" onClick={() => setMergeDialogOpen(true)} data-testid="merge-products-btn">
              <Merge className="w-4 h-4 mr-2" />
              Merge Products
            </Button>
          )}
          <Button variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="manage-categories-btn">
            <Tag className="w-4 h-4 mr-2" />
            Categories
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="add-product-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="product-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                    <SelectTrigger data-testid="product-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(unit => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Base Price</label>
                  <Input type="number" step="0.01" required value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })} data-testid="product-price-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Reorder Level</label>
                <Input type="number" step="0.1" required value={formData.reorder_level} onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })} data-testid="product-reorder-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tax Bracket</label>
                <Select value={formData.tax_bracket} onValueChange={(value) => setFormData({ ...formData, tax_bracket: value })}>
                  <SelectTrigger data-testid="product-tax-bracket-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taxBrackets.map(bracket => <SelectItem key={bracket.value} value={bracket.value}>{bracket.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Barcode / SKU <span className="text-xs text-muted-foreground">(optional — used for POS scanner)</span></label>
                <Input placeholder="e.g. 6291041500213 or BISON-RICE-5KG" value={formData.barcode} onChange={(e) => setFormData({ ...formData, barcode: e.target.value })} data-testid="product-barcode-input" />
              </div>
              <Button type="submit" className="w-full" data-testid="product-submit-btn">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl border border-border shadow-sm mb-4">
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search products by name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
            data-testid="product-search-input"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-4 text-left">
                  <Checkbox 
                    checked={selectedIds.length === filteredProducts.length && filteredProducts.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Product Name</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Category</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Barcode</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Unit</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Base Price</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Tax Bracket</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Current Stock</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                    {searchTerm ? 'No products match your search.' : 'No products yet. Add your first product to get started.'}
                  </td>
                </tr>
              )}
              {paginatedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-muted/30 transition-colors" data-testid="product-row">
                  <td className="px-4 py-4">
                    <Checkbox 
                      checked={selectedIds.includes(product.id)}
                      onCheckedChange={(checked) => handleSelectOne(product.id, checked)}
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{product.category}</td>
                  <td className="px-6 py-4 text-xs font-mono text-muted-foreground">{product.barcode || '—'}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{product.unit}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">RWF {product.base_price.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      product.tax_bracket === 'B' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.tax_bracket || 'B'} {product.tax_bracket === 'B' ? '(18%)' : '(0%)'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`${product.current_stock <= product.reorder_level ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                      {product.current_stock} {product.unit}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} data-testid="edit-product-btn">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id)} data-testid="delete-product-btn">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} pages={totalPages} total={filteredProducts.length} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>

      {/* Merge Products Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={(open) => {
        setMergeDialogOpen(open);
        if (!open) {
          setMergeSource('');
          setMergeTarget('');
          setMergeInventory(true);
        }
      }}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge Products</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Merge a duplicate product into another. All orders, purchases, and price lists referencing the source product will be updated to use the target product. The source product will be deleted.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Source Product (will be deleted)</label>
              <Select value={mergeSource} onValueChange={setMergeSource}>
                <SelectTrigger data-testid="merge-source-select">
                  <SelectValue placeholder="Select source product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.id !== mergeTarget).map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} ({product.unit}) - Stock: {product.current_stock}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Target Product (will be kept)</label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="merge-target-select">
                  <SelectValue placeholder="Select target product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.id !== mergeSource).map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} ({product.unit}) - Stock: {product.current_stock}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="merge-inventory" 
                checked={mergeInventory} 
                onCheckedChange={setMergeInventory}
                data-testid="merge-inventory-checkbox"
              />
              <label htmlFor="merge-inventory" className="text-sm font-medium leading-none cursor-pointer">
                Combine inventory from both products
              </label>
            </div>
            <Button onClick={handleMergeProducts} className="w-full" data-testid="merge-submit-btn">
              <Merge className="w-4 h-4 mr-2" />
              Merge Products
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>Manage Product Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="New category name..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                data-testid="new-category-input"
              />
              <Button onClick={handleAddCategory} data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {categories.map(cat => (
                <div key={cat} className="flex items-center justify-between p-2 rounded hover:bg-muted/30">
                  <span className="text-sm">{cat}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCategory(cat)}
                    data-testid={`delete-category-${cat}`}
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;